/**
 * iter87.199.62 — Onboarding flow (`/start`), Phase A.
 *
 * Operator ratified on 2026-02-16: replace the modal-only
 * AdminFirstRunWizard with a linear, mock-styled onboarding flow
 * living at its own route. Phase A ships steps 0 → 2:
 *
 *   Step 0 · T&C + 18+ consent modal (reuses FirstTimeConsentModal)
 *   Step 1 · Start your crew — crew name + username + email +
 *            inline T&C checkbox + Create Crew CTA. On submit we
 *            call `api.submitLeagueRequest` with tier=free +
 *            admin_is_player=true — the backend fires the magic
 *            link on auto-approve. From this point forward the
 *            admin can always return via that email, even if they
 *            drop off before finishing the tournament setup.
 *   Step 2 · Pick your tournament — reuses FirstRunTemplatePicker
 *            (same flat scroll of tap-and-go presets). Selecting a
 *            preset advances to a Phase-B stub (placeholder) that
 *            will become scoring → invite → summary in iter 62's
 *            Phase B ship.
 *
 * Phase B/C follow-ons:
 *   - Phase B: scoring recap → invite crew (single input with live
 *     list + skip-till-later) → live summary + "Create Tournament"
 *     CTA that fires the actual `/api/tournaments` POST.
 *   - Phase C: retire AdminFirstRunWizard entirely, replace the
 *     dashboard hamburger's "Setup wizard" entry with a direct
 *     link to `/start?step=2` when no tournament exists.
 *
 * NOTE ON /register RETIREMENT: the operator asked to keep
 * `/register` alive during Phase A so nothing else breaks; retire
 * after Phase C lands cleanly.
 */
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, session } from "@/lib/api";
import { proApi } from "@/pro/api";
import { Trophy, Check, ArrowLeft, ArrowRight, Sparkles, Mail, Lock, PlayCircle } from "lucide-react";
import FirstTimeConsentModal from "@/components/FirstTimeConsentModal";
import { hasPlatformConsent, recordPlatformConsent, PLATFORM_CONSENT_VERSION } from "@/lib/platformConsent";
import FirstRunTemplatePicker from "@/components/tournament-setup/FirstRunTemplatePicker";
import IntroVideoModal from "@/components/landing/IntroVideoModal";

/**
 * Shorthand map — turns a preset into a short suffix for the
 * auto-derived tournament name. When the admin leaves the tournament
 * name blank we do `${crewName} — ${shorthandFor(preset)}` so the
 * final name still reads naturally (e.g. "Sunday Squad — CFB").
 */
function shorthandFor(preset) {
    if (!preset) return "";
    const id = preset.id || "";
    // Cover the presets we ship out of the box. Anything unrecognised
    // falls through to the picker label (still short enough).
    if (id === "nfl_weekly")      return "NFL Weekly";
    if (id === "nfl_pot")         return "NFL Pot";
    if (id.startsWith("premier")) return "EPL";
    if (id.startsWith("la_liga")) return "La Liga";
    if (id.startsWith("champions_league")) return "UCL";
    if (id.startsWith("bundes"))  return "Bundesliga";
    if (id.startsWith("world_cup_ko")) return "WC Knockouts";
    if (id === "world_cup")       return "World Cup";
    if (id.startsWith("golf_"))   return preset.label || "Golf";
    if (id === "nwsl_playoffs")   return "NWSL";
    return preset.label || "Tournament";
}
import TermsModal from "@/components/TermsModal";
import PrivacyModal from "@/components/PrivacyModal";

// iter87.470 (operator-reported) — was hardcoded "v2", drifted behind the
// backend's actual current version ("v3", see participants.py
// TERMS_CURRENT_VERSION), so every brand-new admin got immediately
// re-blocked by AdminPanel's separate per-participant terms gate right
// after accepting consent on this exact Step 0 — the stamped version
// never matched what the gate checked against. Now fetched live below
// instead of hardcoded, so it can't drift again. This fallback only
// covers the brief window before that fetch resolves.
const TERMS_VERSION_FALLBACK = "v3";
const STEP_CONSENT   = 0;
const STEP_CREW      = 1;
const STEP_TEMPLATE  = 2;
const STEP_SCORING   = 3;
const STEP_INVITE    = 4;
const STEP_SUMMARY   = 5;
const TOTAL_STEPS = 5;   // step count for progress dots (crew→summary)


/**
 * Scoring preview per preset — read-only summary that mirrors the
 * server defaults the tournament will inherit. Deliberately terse:
 * the admin can drill into full scoring after create in
 * Tournament Settings → Scoring.
 *
 * Pot Pick'em variants (NFL + Club) intentionally OMIT any "Draft"
 * row because pot pickems have no draft — the picks come from pots.
 * Both surface a single "Weekly boosts" line so admins know spread
 * picks layer on top without overwhelming this page.
 */
/**
 * Scoring preview per preset — read-only summary that mirrors the
 * server defaults the tournament will inherit. Deliberately terse:
 * the admin can drill into full scoring after create in
 * Tournament Settings → Scoring.
 *
 * iter87.199.72 — Full audit against backend defaults:
 *   - CFB / NFL Weekly Boost — spread-graded ladder from
 *     `matchday_boost.py::SPREAD_*` constants + picks-per-round.
 *   - NFL Pot / Club Pot — `wins × 3 + draws × 1 + losses × 0`
 *     via `scoring_helpers.py::team_group_points`, then multiplied
 *     by the pot's Power Pick multiplier
 *     (`season_pickem.py::NFL_POT_MULTIPLIERS` / `POT_MULTIPLIERS`).
 *   - World Cup / KO — knockout reach ladder from
 *     `models.py::ScoringRules` defaults (reach_r32=3 → final=15,
 *     champion=20, bronze=15). Entry-stage reach zeroed for KO-only.
 *   - Golf — TWO separate models: lowest-avg (strokes-based, negative
 *     bonuses) vs position-draft (points-based, +3 make cut, top 20
 *     scaled position payout, +10 champions pick).
 *   - Club Season (default fallthrough) — season W/D/L + spread boost
 *     ladder + league-winner bonus (`champion_points=20` from
 *     `_SEASON_SCORING_DEFAULTS`).
 *
 * All values verified against backend as of 2026-02-16. If backend
 * scoring changes, update these previews and the invariant tests
 * in `test_iter87_199_71_pot_pickem_scoring_preview.py` +
 * `test_iter87_199_72_scoring_preview_full_audit.py`.
 */
function scoringPreview(preset) {
    if (!preset) return [];
    const id = preset.id;
    if (id === "nfl_weekly") {
        // iter87.199.72 → iter87.199.79 — nfl_boost_api default lowered
        // from 10 → 8 picks/week (operator lock 2026-02-16). Admin can
        // override 1..25 at wizard time or on Scoring tab.
        return [
            ["Correct pick against the spread", "+6"],
            ["Correct straight-up (missed spread)", "+2"],
            ["Loss that still covers", "0"],
            ["Missed both", "−2"],
            ["Picks per week", "8 (admin-adjustable)"],
            ["Picks lock at kickoff", "per game"],
        ];
    }
    if (id === "premier_weekly" || id === "la_liga_weekly") {
        // iter87.199.179 — EPL/La Liga Weekly Spread (boost-only, same
        // shape as NFL Weekly) fell through to the generic Club
        // Season Draft preview below (Team win/draw/loss + League
        // winner bonus + Power Pick) — none of which exist on this
        // template. Mirrors nfl_weekly exactly; default 5
        // picks/week per `matchday_boost.py::_picks_per_round`.
        return [
            ["Correct pick against the spread", "+6"],
            ["Correct straight-up (missed spread)", "+2"],
            ["Loss that still covers", "0"],
            ["Missed both", "−2"],
            ["Picks per week", "5 (admin-adjustable)"],
            ["Picks lock at kickoff", "per game"],
        ];
    }
    if (id === "nfl_pot") {
        // iter87.199.71 — Preview was showing "+1 × pot multiplier" for
        // team wins, but backend scoring (nfl_engine + team_group_points)
        // pays `wins × 3 + ties × 1`, then multiplies drafted teams by
        // the pot's Power Pick multiplier. Match the real values.
        return [
            ["Team win", "+3 × pot multiplier"],
            ["Team tie (rare)", "+1 × pot multiplier"],
            ["Pot multipliers", "1× / 1.25× / 1.5× / 2×"],
            ["Weekly Spread Boost", "+6 / +2 / 0 / −2"],
            ["Power Pick", "built in"],
        ];
    }
    if (id?.endsWith("_pot")) {
        // iter87.199.71 — Club (soccer) pot pickem. Backend scoring
        // pays `wins × 3 + draws × 1 + losses × 0` via
        // team_group_points, then multiplies drafted teams by the
        // pot's Power Pick multiplier. Losses row added explicitly
        // (was implied but not shown — operator wanted 3/1/0 W/D/L
        // spelled out symmetric with NFL Pot).
        return [
            ["Team win", "+3 × pot multiplier"],
            ["Team draw", "+1 × pot multiplier"],
            ["Team loss", "0"],
            ["Pot multipliers", "1× / 1.5× / 2×"],
            ["Matchday Boost", "+6 / +2 / 0 / −2"],
            ["Power Pick", "built in"],
        ];
    }
    if (id === "world_cup") {
        // iter87.199.72 — 48-team WC. Group stage first, then KO
        // ladder from ScoringRules defaults. Cumulative advancement is
        // ON by default, so the "reach a stage" points STACK as the
        // team survives — a champion earns r32+r16+qf+sf+final+champion.
        return [
            ["Group win / draw / loss", "+3 / +1 / 0"],
            ["Reach Round of 32", "+3"],
            ["Reach Round of 16", "+5"],
            ["Reach Quarterfinals", "+8"],
            ["Reach Semifinals", "+12"],
            ["Reach Final", "+15"],
            ["Champion", "+20"],
            ["Bronze medallist (3rd-place playoff)", "+15"],
            ["Knockout bonus pick (locks by stage)", "+30 → +5 (decay)"],
        ];
    }
    if (id === "world_cup_ko") {
        // iter87.199.72 — Knockout-only. Entry stage (R32) is zeroed
        // by `_zero_entry_stage_reach_points` because every drafted
        // team automatically starts there — awarding reach points for
        // the entry stage would just be a flat freebie for everyone.
        return [
            ["Reach Round of 32 (entry — everyone)", "0"],
            ["Reach Round of 16", "+5"],
            ["Reach Quarterfinals", "+8"],
            ["Reach Semifinals", "+12"],
            ["Reach Final", "+15"],
            ["Champion", "+20"],
            ["Bronze medallist (3rd-place playoff)", "+15"],
            ["Knockout bonus pick (locks by stage)", "+30 → +5 (decay)"],
        ];
    }
    if (id === "mlb_playoffs") {
        // iter87.213.9 (operator-corrected) — Team Pick Pot scores PER
        // GAME your rostered team plays, NOT bracket-round advancement
        // (that was the iter87.213.8 bug: still wrong, just less wrong).
        // Champions Pick simplified to a single WS-champion slot.
        return [
            ["Your team wins a game", "+6 × pot multiplier"],
            ["Your team loses a game", "−2 × pot multiplier"],
            ["Pot multipliers (Power Pick team)", "1× / 1.25× / 1.5×"],
            ["Champions Pick — World Series champion", "+10"],
            ["Series Predictor — correct team", "+6 (+4 if exact game #, so +10)"],
            ["Series Predictor — wrong team", "−2 (nets 0 if game # still right)"],
        ];
    }
    if (id === "nba_playoffs") {
        // iter87.225 — sibling of mlb_playoffs above. Play-In games
        // count toward Team Pick Pot but are excluded from Series
        // Predictor (single-elimination, not a "series").
        return [
            ["Your team wins a game (incl. Play-In)", "+6 × pot multiplier"],
            ["Your team loses a game (incl. Play-In)", "−2 × pot multiplier"],
            ["Pot multipliers (Power Pick team)", "1× / 1.25× / 1.5× / 2×"],
            ["Champions Pick — NBA champion", "+10"],
            ["Series Predictor — correct team", "+6 (+4 if exact game #, so +10)"],
            ["Series Predictor — wrong team", "−2 (nets 0 if game # still right)"],
        ];
    }
    if (id === "nwsl_playoffs") {
        // iter87.361 — Pure pick'em, NO Team Pick Pot at all (unlike
        // MLB/NBA above) — fixed 8-team bracket, no reseeding.
        // Champions Pick + Dark Horse Pick lock at Round 1 (QF)
        // kickoff; Match Predictor locks per game at its own kickoff.
        return [
            ["Champions Pick — any bracket team", "+10"],
            ["Dark Horse Pick — seed 5-8 only, per win", "+5 per win"],
            ["Playoff Predictor — correct winner", "+3"],
            ["Playoff Predictor — how it's decided (Reg/ET/PK)", "+2"],
            ["Playoff Predictor — total goals over/under 2.5", "+5"],
            ["Champions + Dark Horse lock", "Round 1 kickoff"],
        ];
    }
    if (id?.startsWith("golf_") && id.endsWith("_avg")) {
        // iter87.199.72 — Lowest-average pot pickem. STROKES-based (not
        // points): whoever's 6-golfer portfolio has the lowest total
        // strokes wins. Bonuses subtract strokes from the picker's own
        // total.
        return [
            ["Portfolio", "6 golfers · lowest total strokes wins"],
            ["Power Pick bonus", "−2 strokes per round played"],
            ["Champions Pick bonus", "−4 strokes off total if pick wins"],
            ["Missed cut", "no rebate — golfer's cut-day score stays"],
        ];
    }
    if (id?.startsWith("golf_") && id.endsWith("_draft")) {
        // iter87.199.72 — Position-draft pot pickem. POINTS-based per
        // golf_engine.py::DRAFT_SCORING_DEFAULTS.
        return [
            ["Golfer makes cut", "+3"],
            ["1st / 2nd / 3rd position", "+20 / +17 / +15"],
            ["4th–10th position", "+10 each"],
            ["11th–20th position", "+5 each"],
            ["Champions Pick bonus", "+10 if pick wins the event"],
        ];
    }
    // iter87.199.72 — Club season draft (fallthrough). Default matchday
    // boost for newly-created club season tournaments is spread mode
    // (iter87.30 — 6/2/0/-2 ladder). `champion_points=20` fires as a
    // league-winner standings bonus when the season completes.
    return [
        ["Team win", "+3"],
        ["Team draw", "+1"],
        ["Team loss", "0"],
        ["League winner bonus", "+20 at season end"],
        ["Matchday Boost", "+6 / +2 / 0 / −2 (on by default)"],
        ["Power Pick", "optional — multiplies season points"],
    ];
}


export default function Start({
    mode = "free",
    initialEmail = "",
    initialUsername = "",
    emailLocked = false,
    lockedEmailNote = null,
} = {}) {
    const navigate = useNavigate();

    // iter87.199.99 — Auto-skip Step 0 if the user has already accepted
    // TCR platform consent in this browser (Path A: localStorage-only,
    // no server-side record). The per-league audit stamp
    // (`terms_accepted_at`, `age_confirmed_at` on the league doc,
    // iter87.199.69) is still populated below from the stored consent
    // shape, so compliance record-of-truth is unaffected.
    const hasPriorConsent = useMemo(() => hasPlatformConsent(), []);

    // Flow state — kept flat so a future refactor can serialize to
    // sessionStorage for resume-after-refresh if the operator asks.
    const [step, setStep] = useState(hasPriorConsent ? STEP_CREW : STEP_CONSENT);
    const [crewName, setCrewName]   = useState("");
    const [username, setUsername]   = useState(initialUsername);
    const [email, setEmail]         = useState(initialEmail);
    const [creating, setCreating]   = useState(false);
    const [createdLeague, setCreatedLeague] = useState(null);
    // iter87.468 — "Watch how it works" modal, reuses the same intro
    // video as the landing page.
    const [introVideoOpen, setIntroVideoOpen] = useState(false);
    // iter87.199.69 — Pro+ callers pass their Clerk email pre-locked
    // so we render the field read-only and skip the type-your-email
    // affordance. Free tier ignores this and lets the user type.
    const isProPlus = mode === "pro_plus";
    // iter87.234.6 (operator ask) — Pro+ callers land here via the
    // dedicated `/rivalry-pro/new` route (see ProPlusNewRivalry.jsx),
    // NOT the marketing site. Every "exit the wizard" affordance below
    // (Step 0 Back, consent Decline, "Back to home") must return them
    // to their Pro+ hub, not the free-tier landing page.
    const exitHref = isProPlus ? "/rivalry-pro" : "/";

    // Step 2 — reused FirstRunTemplatePicker state.
    const [selectedPreset, setSelectedPreset] = useState(null);
    // iter87.199.65 — Optional tournament-name override. Empty ⇒
    // auto-derived from `${crewName} — ${preset.shorthand}` on
    // commit. Prevents the "tournament name required" error while
    // still letting the admin personalize the name up front.
    const [tournamentName, setTournamentName] = useState("");
    // iter87.199.66 Phase B — invite emails collected on Step 4 and
    // sent by `commitAll` AFTER submitLeagueRequest + createTournament
    // succeed. Nothing leaves the client until the admin commits.
    const [invitedEmails, setInvitedEmails] = useState([]);
    const [inviteDraft, setInviteDraft] = useState("");
    const [visibilityOverrides, setVisibilityOverrides] = useState({});
    const [disabledTemplates, setDisabledTemplates] = useState([]);
    const [clubBindings, setClubBindings] = useState(null);
    const [golfBindings, setGolfBindings] = useState(null);

    // Bootstrapping data for the picker — fetched once when we hit
    // step 2 so we don't block the landing→consent transition.
    React.useEffect(() => {
        if (step !== STEP_TEMPLATE) return;
        api.getTemplateVisibility()
            .then((r) => setVisibilityOverrides(r?.overrides || {}))
            .catch(() => setVisibilityOverrides({}));
        // iter87.199.165 — Site Admin's Template Availability toggle
        // (disabled_templates) previously had zero effect on this
        // first-run picker — only the Platform Admin visibility map
        // was consulted. Public /site-settings carries this safely.
        api.getSiteSettings()
            .then((r) => setDisabledTemplates(Array.isArray(r?.disabled_templates) ? r.disabled_templates : []))
            .catch(() => setDisabledTemplates([]));
        api.getClubSeasonBindings()
            .then((d) => setClubBindings(d?.bindings || []))
            .catch(() => setClubBindings([]));
        // Golf bindings are optional — fall through to empty on error
        // so the picker still renders soccer/NFL/CFB presets.
        api.getGolfBindings?.()
            .then((d) => setGolfBindings(d?.bindings || []))
            .catch(() => setGolfBindings([]));
    }, [step]);

    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
    const [termsVersion, setTermsVersion] = useState(TERMS_VERSION_FALLBACK);

    // Fetch the backend's live current terms version once on mount so the
    // stamp we send always matches what AdminPanel's own gate checks against.
    React.useEffect(() => {
        api.getTermsVersion()
            .then((r) => { if (r?.current_version) setTermsVersion(r.current_version); })
            .catch(() => {});
    }, []);

    const canSubmitCrew = crewName.trim() && username.trim() && email.trim() && !creating;

    const submitStep1 = () => {
        if (!canSubmitCrew) return;
        // iter87.199.64 — Do NOT hit the backend yet. The whole
        // Rivalry + tournament pair commits atomically at the end of
        // the flow (Step 2's Next → in Phase A; Phase B will move the
        // commit to Step 5's "Create Tournament"). This avoids
        // orphaned crews when the admin abandons mid-flow.
        setStep(STEP_TEMPLATE);
    };

    const commitAll = async () => {
        // Atomic-ish commit — Rivalry first (to get a league_id), then
        // tournament. If tournament creation fails the crew stays alive
        // and the admin can retry from the dashboard next time via the
        // magic link. Orphaned tournaments are impossible because the
        // tournament call only runs on a successful crew POST.
        if (!selectedPreset) {
            toast.error("Pick a tournament first");
            return;
        }
        setCreating(true);
        try {
            // iter87.199.69 — Two commit paths, one flow. Free-tier
            // hits the public Turnstile-gated `/league-requests`
            // endpoint; Pro+ hits the Clerk-authed `/rivalry-pro/leagues`
            // endpoint (higher cap, no Turnstile). Both return a
            // symmetric shape (`league_id`/`id`, `admin_participant_id`,
            // `admin_username`, `admin_email`, `invite_code`) so the
            // session-save + navigate tail below is identical.
            let crewRes;
            if (isProPlus) {
                const proRes = await proApi.createLeague({
                    league_name: crewName.trim(),
                    admin_username: username.trim(),
                    strict_invites: true,
                    terms_accepted: true,
                    terms_version: termsVersion,
                    age_confirmed: true,
                });
                // Normalize to the Free-tier shape used below.
                crewRes = {
                    auto_approved: true,
                    league_id: proRes.id,
                    league_name: proRes.league_name,
                    invite_code: proRes.invite_code,
                    admin_participant_id: proRes.admin_participant_id,
                    admin_username: proRes.admin_username,
                    admin_email: proRes.admin_email,
                };
            } else {
                crewRes = await api.submitLeagueRequest({
                    league_name: crewName.trim(),
                    admin_username: username.trim(),
                    admin_email: email.trim().toLowerCase(),
                    admin_is_player: true,
                    terms_accepted: true,
                    terms_version: termsVersion,
                    age_confirmed: true,
                    requested_tier: "free",
                    trial_code: null,
                });
            }
            setCreatedLeague(crewRes || {});
            if (!crewRes?.auto_approved) {
                // Rare — non-auto-approve path. Land on the wait screen.
                toast.success("Crew request submitted — we'll email you when approved.");
                setStep(STEP_CONSENT - 1);
                setCreating(false);
                return;
            }
            // Auto-approved — create the tournament immediately using
            // the picked preset. Same payload shape the wizard used.
            // iter87.199.67 — Save the local session BEFORE the
            // createTournament + navigate chain. Without this the
            // browser has no participant identity for the newly-created
            // Rivalry, so the follow-up navigate("/league/{id}") gets
            // bounced back to "/" with a "you're not in this rivalry"
            // toast on the first paint of LeagueDashboard.
            if (crewRes.admin_participant_id) {
                session.save({
                    league: {
                        id: crewRes.league_id,
                        name: crewRes.league_name,
                        invite_code: crewRes.invite_code,
                    },
                    participant: {
                        id: crewRes.admin_participant_id,
                        username: crewRes.admin_username,
                        email: crewRes.admin_email,
                        is_admin: true,
                    },
                });
            }
            const derivedName =
                (tournamentName && tournamentName.trim()) ||
                `${crewName.trim()} — ${shorthandFor(selectedPreset)}`;
            const payload = {
                league_id: crewRes.league_id,
                name: derivedName,
                template: selectedPreset.template,
                ...(selectedPreset.extras || {}),
            };
            // iter87.199.67 — Mirror AdminFirstRunWizard payload
            // transforms so pot-pickem and dynamic-binding presets
            // actually configure the tournament correctly. Previously
            // the raw "pickem:nfl_season_api" / "dyn:<id>" template
            // strings were sent through as-is, which quietly bypassed
            // the pickem block on the backend and left NFL Pot tournaments
            // rendering with draft UI (Kick-off-the-draft banner + DRAFT
            // section header) because tournament.pickem.mode was never
            // set to "pot".
            const rawTpl = payload.template || "";
            if (rawTpl.startsWith("pickem:")) {
                payload.template = rawTpl.slice(7);
                payload.pickem_mode = "pot";
            } else if (rawTpl.startsWith("dyn:")) {
                payload.template = "club_season_api";
                payload.club_binding_id = rawTpl.slice(4);
            } else if (rawTpl === "mlb_playoffs_api") {
                // iter87.213.7 — same pot-pickem wiring NFL/club leagues
                // needed (iter87.199.67): MLB is pot-pick'em only.
                payload.pickem_mode = "pot";
            } else if (rawTpl === "nba_playoffs_api") {
                // iter87.225 — sibling of the MLB branch above.
                payload.pickem_mode = "pot";
            }
            // iter87.230 → iter87.374 → iter87.437 (bug found while
            // migrating CFB) — NFL Playoffs retired pot mode in
            // iter87.374 and CFB followed in iter87.437, but THIS entry
            // point (the separate "First Run" flow, sibling of
            // TournamentSetup.jsx) was never updated — it was still
            // unconditionally stamping `pickem_mode: "pot"` for BOTH,
            // which would have silently put any NFL/CFB Playoffs league
            // created here back into the retired Team Pick Pot draft.
            // Neither sends `pickem_mode` anymore (same as NWSL).
            await api.createTournament(payload);

            // Fire invite emails AFTER the crew is live. Best-effort:
            // if this fails the admin still has a working Rivalry +
            // tournament and can retry from the dashboard.
            if (invitedEmails.length > 0) {
                try {
                    await api.sendInviteEmails(crewRes.league_id, invitedEmails);
                } catch (inviteErr) {
                    // Non-fatal — surface but don't block navigation.
                    toast.error("Rivalry live but invites failed to send. Retry from Admin → Invites.");
                }
            }
            toast.success("Crew live — check your inbox for the setup link.");
            navigate(`/league/${crewRes.league_id}`);
        } catch (err) {
            // iter87.199.68 — Free-tier league-cap pre-check returns
            // 402 with a `code: "league_cap_reached"` detail so we can
            // surface a specific, actionable toast instead of the
            // generic "something went wrong". The user stays on Step 1
            // so they can adjust the email (e.g. use a different one)
            // or head to their existing Rivalry.
            // iter87.199.69 — Pro+ create-league returns 409 with a
            // slightly different detail shape (`error: "league_cap_reached"`).
            // Handle both so the Pro+ path shows the same friendly toast.
            const detail = err.response?.data?.detail;
            const status = err.response?.status;
            const isCapDetail =
                detail && typeof detail === "object" &&
                (detail.code === "league_cap_reached" || detail.error === "league_cap_reached");
            if ((status === 402 || status === 409) && isCapDetail) {
                toast.error(detail.message || "You've hit your rivalry cap.");
                setCreating(false);
                return;
            }
            // Safe extraction — detail may be a string, an object with
            // a `.message`, or absent. Never toast a raw object.
            const msg = typeof detail === "string"
                ? detail
                : (detail && typeof detail === "object" && detail.message)
                    ? detail.message
                    : "Something went wrong. Please try again.";
            toast.error(msg);
        }
        setCreating(false);
    };

    // Progress dots — hidden on step 0 (consent overlay).
    const dots = useMemo(() => {
        const visible = step >= STEP_CREW && step <= STEP_SUMMARY;
        return { visible, current: Math.max(0, step - STEP_CREW) };
    }, [step]);

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="onboarding-flow">
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-cyber/10 blur-3xl rounded-full pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-volt/10 blur-3xl rounded-full pointer-events-none" />

            {/* Header — back button on steps ≥1, brand always. */}
            <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-5">
                <button
                    type="button"
                    onClick={() => (step > STEP_CREW ? setStep((s) => s - 1) : navigate(exitHref))}
                    className="brutal-btn-ghost text-xs py-1.5 px-3"
                    data-testid="onboarding-back-btn"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 bg-cyber flex items-center justify-center border-2 border-black">
                        <Trophy className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <span className="font-display font-black text-xs sm:text-sm tracking-tight">TINY CREW RIVALRIES</span>
                </div>
                <button
                    type="button"
                    onClick={() => setIntroVideoOpen(true)}
                    className="brutal-btn-ghost text-[10px] sm:text-xs py-1.5 px-2 sm:px-3 !border-cyber/40 text-cyber hover:bg-cyber/10"
                    data-testid="onboarding-watch-how-it-works-btn"
                >
                    <PlayCircle className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Watch how it works</span>
                </button>
            </header>

            <IntroVideoModal open={introVideoOpen} onOpenChange={setIntroVideoOpen} />

            {/* Step 0 — consent modal. Auto-opens on mount; accepting
                moves to Step 1, declining bounces back to landing. */}
            {step === STEP_CONSENT && (
                <FirstTimeConsentModal
                    open
                    title="Start a Rivalry"
                    submitting={false}
                    onAccept={() => { recordPlatformConsent(); setStep(STEP_CREW); }}
                    onDecline={() => navigate(exitHref)}
                    onOpenTerms={() => setTermsModalOpen(true)}
                    onOpenPrivacy={() => setPrivacyModalOpen(true)}
                    data-testid="onboarding-consent"
                />
            )}

            {/* Main slot — mock-styled card wrapper on all content steps. */}
            <main className="relative z-10 max-w-md mx-auto px-5 sm:px-8 pb-16 pt-2">
                {/* Step 1 — Start your crew */}
                {step === STEP_CREW && (
                    <section data-testid="onboarding-step-crew" className="space-y-5">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">
                            Step 1 · Sign up
                        </div>
                        <h1 className="font-display font-black text-4xl leading-[0.95] tracking-tight">
                            Start your<br />crew
                        </h1>

                        <div>
                            <label htmlFor="crew-name" className="block text-[10px] uppercase tracking-wider text-zinc-500 font-black mb-1.5">Crew name</label>
                            <input
                                id="crew-name"
                                type="text"
                                value={crewName}
                                onChange={(e) => setCrewName(e.target.value)}
                                placeholder="Sunday Squad"
                                className="brutal-input w-full text-sm"
                                maxLength={80}
                                data-testid="onboarding-crew-name-input"
                            />
                        </div>

                        <div>
                            <label htmlFor="username" className="block text-[10px] uppercase tracking-wider text-zinc-500 font-black mb-1.5">Your username</label>
                            <input
                                id="username"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="alex_r"
                                className="brutal-input w-full text-sm"
                                maxLength={40}
                                data-testid="onboarding-username-input"
                            />
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-[10px] uppercase tracking-wider text-zinc-500 font-black mb-1.5 flex items-center gap-1.5">
                                Your email
                                {emailLocked && <Lock className="w-3 h-3 text-cyber" data-testid="onboarding-email-locked-icon" />}
                            </label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="alex@rivals.co"
                                readOnly={emailLocked}
                                disabled={emailLocked}
                                className={`brutal-input w-full text-sm ${email.trim() ? "border-cyber/50 bg-cyber/[0.05]" : ""} ${emailLocked ? "opacity-70 cursor-not-allowed" : ""}`}
                                data-testid="onboarding-email-input"
                            />
                            {emailLocked && lockedEmailNote && (
                                <p className="text-[10px] text-zinc-500 mt-1" data-testid="onboarding-email-locked-note">{lockedEmailNote}</p>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={submitStep1}
                            disabled={!canSubmitCrew}
                            className="brutal-btn-primary w-full text-sm py-3 disabled:opacity-40"
                            data-testid="onboarding-create-crew-btn"
                        >
                            Continue <ArrowRight className="w-4 h-4" />
                        </button>

                        <p className="text-[11px] text-zinc-500 leading-relaxed">
                            Your crew and tournament go live together at the end — nothing is created until you tap the final button. You already accepted the terms and confirmed you're 18+.
                        </p>
                    </section>
                )}

                {/* Wait state — non-auto-approved requests. Rare given
                    the Cloudflare Turnstile auto-approve fast path, but
                    keeps the flow honest. */}
                {step === STEP_CONSENT - 1 && (
                    <section data-testid="onboarding-await-approval" className="space-y-4 text-center pt-8">
                        <Sparkles className="w-8 h-8 text-cyber mx-auto" />
                        <h1 className="font-display font-black text-3xl">Request submitted</h1>
                        <p className="text-sm text-zinc-400 leading-relaxed">
                            We'll email you as soon as your crew is approved. Feel free to close this tab — the link will land in your inbox.
                        </p>
                        <button type="button" onClick={() => navigate(exitHref)} className="brutal-btn-ghost text-xs" data-testid="onboarding-await-return-btn">
                            Back to home
                        </button>
                    </section>
                )}

                {/* Step 2 — Pick your tournament */}
                {step === STEP_TEMPLATE && (
                    <section data-testid="onboarding-step-template" className="space-y-5">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">
                            Step 2 · Pick a tournament
                        </div>
                        <h1 className="font-display font-black text-3xl leading-tight tracking-tight flex items-center gap-2">
                            <Trophy className="w-6 h-6 text-cyber" /> Pick your tournament
                        </h1>
                        <p className="text-sm text-zinc-400 -mt-2">Ready to go, out of the box.</p>

                        <FirstRunTemplatePicker
                            selectedPresetId={selectedPreset?.id || null}
                            visibilityOverrides={visibilityOverrides}
                            disabledTemplates={disabledTemplates}
                            clubBindings={clubBindings}
                            golfBindings={golfBindings}
                            onPick={(preset) => setSelectedPreset(preset)}
                        />

                        {/* iter87.199.65 — Optional tournament-name
                            input with a soft cyber glow so it draws
                            the eye once a preset is picked. Left blank
                            → auto-derived on commit as
                            "${crewName} — ${shorthand}" (e.g. "Sunday
                            Squad — CFB"). Backend used to raise a
                            "tournament name required" error on empty
                            submits; now impossible. */}
                        {selectedPreset && (
                            <div className="pt-2" data-testid="onboarding-tournament-name-block">
                                <label htmlFor="tournament-name" className="block text-[10px] uppercase tracking-wider text-zinc-500 font-black mb-1.5">
                                    Name your tournament <span className="text-zinc-600 normal-case tracking-normal font-normal">— optional</span>
                                </label>
                                <input
                                    id="tournament-name"
                                    type="text"
                                    value={tournamentName}
                                    onChange={(e) => setTournamentName(e.target.value)}
                                    placeholder={`${crewName.trim() || "Our"} — ${shorthandFor(selectedPreset)}`}
                                    maxLength={80}
                                    className="brutal-input w-full text-sm border-cyber/60 bg-cyber/[0.04] focus:border-cyber shadow-[0_0_18px_-8px_rgba(255,204,0,0.55)] focus:shadow-[0_0_24px_-6px_rgba(255,204,0,0.75)]"
                                    data-testid="onboarding-tournament-name-input"
                                />
                                <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
                                    Leave blank and we'll name it after your crew and the format.
                                </p>
                            </div>
                        )}

                        {/* Step 2 CTA now just advances to Scoring
                            preview (Step 3). Actual commit lives on
                            Step 5's "Create Tournament". */}
                        <button
                            type="button"
                            onClick={() => {
                                if (!selectedPreset) { toast.error("Pick a tournament first"); return; }
                                setStep(STEP_SCORING);
                            }}
                            disabled={!selectedPreset}
                            className="brutal-btn-primary w-full text-sm py-3 disabled:opacity-40"
                            data-testid="onboarding-template-next-btn"
                        >
                            Continue <ArrowRight className="w-4 h-4" />
                        </button>
                    </section>
                )}

                {/* Step 3 — Scoring preview (read-only). */}
                {step === STEP_SCORING && (
                    <section data-testid="onboarding-step-scoring" className="space-y-5">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">
                            Step 3 · Scoring
                        </div>
                        <h1 className="font-display font-black text-3xl leading-tight tracking-tight flex items-center gap-2">
                            <Sparkles className="w-6 h-6 text-cyber" /> Recommended scoring
                        </h1>
                        <p className="text-sm text-zinc-400 -mt-2">
                            Dialed in for {selectedPreset?.label || "your format"}. Every value below is editable after create.
                        </p>

                        <div className="rounded-xl border border-white/10 bg-[#0e0e0e] p-4 space-y-0.5" data-testid="onboarding-scoring-rows">
                            {scoringPreview(selectedPreset).map(([label, value]) => (
                                <div key={label} className="flex items-baseline justify-between gap-3 py-2 border-b border-white/[0.05] last:border-b-0">
                                    <span className="text-xs text-zinc-300">{label}</span>
                                    <span className="text-xs font-bold text-white text-right shrink-0">{value}</span>
                                </div>
                            ))}
                        </div>

                        <p className="text-[11px] text-zinc-500 leading-relaxed">
                            Fine-tune every value in <b className="text-white">Tournament Settings → Scoring</b> after your crew is live.
                        </p>

                        <button
                            type="button"
                            onClick={() => setStep(STEP_INVITE)}
                            className="brutal-btn-primary w-full text-sm py-3"
                            data-testid="onboarding-scoring-next-btn"
                        >
                            Continue <ArrowRight className="w-4 h-4" />
                        </button>
                    </section>
                )}

                {/* Step 4 — Invite crew (collect emails locally). */}
                {step === STEP_INVITE && (
                    <section data-testid="onboarding-step-invite" className="space-y-5">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">
                            Step 4 · Invite your crew
                        </div>
                        <h1 className="font-display font-black text-3xl leading-tight tracking-tight flex items-center gap-2">
                            <Mail className="w-6 h-6 text-volt" /> Bring the crew
                        </h1>
                        <p className="text-sm text-zinc-400 -mt-2">
                            {invitedEmails.length} of 20 invited · one tap and they're in.
                        </p>

                        <div>
                            <label htmlFor="invite-email" className="block text-[10px] uppercase tracking-wider text-zinc-500 font-black mb-1.5">Add email</label>
                            <div className="flex gap-2">
                                <input
                                    id="invite-email"
                                    type="email"
                                    value={inviteDraft}
                                    onChange={(e) => setInviteDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            const em = inviteDraft.trim().toLowerCase();
                                            if (em && !invitedEmails.includes(em)) {
                                                setInvitedEmails((prev) => [...prev, em]);
                                                setInviteDraft("");
                                            }
                                        }
                                    }}
                                    placeholder="friend@email.com"
                                    className="brutal-input flex-1 text-sm"
                                    data-testid="onboarding-invite-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        const em = inviteDraft.trim().toLowerCase();
                                        if (em && !invitedEmails.includes(em)) {
                                            setInvitedEmails((prev) => [...prev, em]);
                                            setInviteDraft("");
                                        }
                                    }}
                                    disabled={!inviteDraft.trim() || invitedEmails.length >= 20}
                                    className="brutal-btn-ghost text-xs px-3 disabled:opacity-40 shrink-0"
                                    data-testid="onboarding-invite-add-btn"
                                >
                                    Add
                                </button>
                            </div>
                        </div>

                        {invitedEmails.length > 0 && (
                            <div className="space-y-1.5" data-testid="onboarding-invite-list">
                                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-black">On the list</div>
                                {invitedEmails.map((em) => {
                                    const initials = em.split("@")[0].slice(0, 2).toUpperCase();
                                    return (
                                        <div key={em} className="rounded-lg bg-[#0e0e0e] border border-white/8 px-2.5 py-2 flex items-center gap-2">
                                            <div className="w-7 h-7 rounded-full bg-volt/20 border border-volt/40 flex items-center justify-center shrink-0">
                                                <span className="text-[10px] font-black text-volt">{initials}</span>
                                            </div>
                                            <span className="flex-1 text-xs text-white truncate">{em}</span>
                                            <span className="text-[9px] font-black uppercase text-blaze shrink-0">Pending</span>
                                            <button
                                                type="button"
                                                onClick={() => setInvitedEmails((prev) => prev.filter((e) => e !== em))}
                                                className="text-zinc-500 hover:text-heat text-lg leading-none shrink-0"
                                                aria-label={`Remove ${em}`}
                                                data-testid={`onboarding-invite-remove-${em}`}
                                            >×</button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={() => setStep(STEP_SUMMARY)}
                            className="brutal-btn-primary w-full text-sm py-3"
                            data-testid="onboarding-invite-next-btn"
                        >
                            Continue <ArrowRight className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => setStep(STEP_SUMMARY)}
                            className="w-full text-[11px] text-zinc-500 hover:text-cyber underline-offset-2 hover:underline py-2"
                            data-testid="onboarding-invite-skip-btn"
                        >
                            Skip — I'll invite the crew later
                        </button>
                    </section>
                )}

                {/* Step 5 — Summary + final commit. */}
                {step === STEP_SUMMARY && (
                    <section data-testid="onboarding-step-summary" className="space-y-5">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">
                            Step 5 · Ready to launch
                        </div>
                        <h1 className="font-display font-black text-3xl leading-tight tracking-tight">
                            You're all set
                        </h1>
                        <p className="text-sm text-zinc-400 -mt-2">
                            One tap and your crew, tournament, and invites all go live together.
                        </p>

                        <div className="rounded-xl border border-cyber/40 bg-cyber/[0.04] p-4 space-y-2.5" data-testid="onboarding-summary-recap">
                            <SummaryRow label="Crew" value={crewName.trim()} />
                            <SummaryRow label="You" value={`${username.trim()} · ${email.trim()}`} />
                            <SummaryRow
                                label="Tournament"
                                value={(tournamentName.trim() || `${crewName.trim()} — ${shorthandFor(selectedPreset)}`)}
                            />
                            <SummaryRow label="Format" value={selectedPreset?.label || "—"} />
                            <SummaryRow label="Invites" value={invitedEmails.length > 0 ? `${invitedEmails.length} pending` : "None yet"} />
                        </div>

                        <div className="rounded-xl border border-white/10 bg-[#0e0e0e] p-4 space-y-2 text-[11px] text-zinc-400 leading-relaxed">
                            <p className="flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 text-cyber mt-0.5 shrink-0" /><span><b className="text-white">Where things live:</b> boosts, scoring and season length are in <b className="text-white">Tournament Settings</b>. Re-invite anyone from <b className="text-white">Admin → Invites</b>.</span></p>
                            <p className="flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 text-volt mt-0.5 shrink-0" /><span><b className="text-white">Magic link:</b> we'll email one to <b className="text-white">{email.trim()}</b> so you can jump back in from any device.</span></p>
                        </div>

                        <button
                            type="button"
                            onClick={commitAll}
                            disabled={creating}
                            className="brutal-btn-primary w-full text-sm py-3 disabled:opacity-40"
                            data-testid="onboarding-create-tournament-btn"
                        >
                            {creating ? "Creating…" : <>Create tournament <ArrowRight className="w-4 h-4" /></>}
                        </button>
                    </section>
                )}

                {/* Progress dots — bottom-aligned so they don't fight
                    the phone-frame vibe of the mocks. */}
                {dots.visible && (
                    <div className="flex justify-center gap-1.5 mt-8" data-testid="onboarding-progress-dots">
                        {Array.from({ length: STEP_SUMMARY - STEP_CREW + 1 }).map((_, i) => (
                            <span
                                key={i}
                                className={`h-1.5 rounded-full transition-all ${
                                    i === dots.current ? "w-6 bg-cyber" : "w-1.5 bg-white/20"
                                }`}
                            />
                        ))}
                    </div>
                )}
            </main>

            <TermsModal open={termsModalOpen} onClose={() => setTermsModalOpen(false)} />
            <PrivacyModal open={privacyModalOpen} onClose={() => setPrivacyModalOpen(false)} />
        </div>
    );
}


// Named exports for pytest scraping — the invariant suite reads
// these constants to lock the step ordering + total count.
export const ONBOARDING_STEPS = { STEP_CONSENT, STEP_CREW, STEP_TEMPLATE, STEP_SCORING, STEP_INVITE, STEP_SUMMARY, TOTAL_STEPS };


function SummaryRow({ label, value }) {
    return (
        <div className="flex items-baseline justify-between gap-3 text-xs">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">{label}</span>
            <span className="text-white font-bold text-right truncate">{value}</span>
        </div>
    );
}
